
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/auth";
import { Button } from "@/components/ui/button";
import { LogIn, LogOut, User as UserIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import CloudLoginButton from "./CloudLoginButton";

const AuthStatus = () => {
  const { user, loading, signOut } = useAuth();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  // Display a skeleton UI while loading
  if (loading) {
    return (
      <div className="animate-pulse flex items-center space-x-2">
        <div className="h-9 w-24 bg-mono-slateGray/20 rounded"></div>
      </div>
    );
  }

  // If not logged in, show login button
  if (!user) {
    return isHomePage ? (
      <CloudLoginButton />
    ) : (
      <Link to="/auth">
        <Button 
          variant="ghost" 
          size="sm" 
          className="ml-auto rounded-full hover:bg-mono-outerSpace/30 hover:text-mono-platinum transition-colors duration-200 text-mono-platinum"
          data-spark="true"
        >
          <LogIn className="mr-2 h-4 w-4" />
          Login
        </Button>
      </Link>
    );
  }

  // Display user info and dropdown for logged in users
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="relative h-9 rounded-full flex items-center px-2 space-x-2 hover:bg-mono-onyx/50 transition-colors duration-200"
        >
          <Avatar className="h-8 w-8 border border-mono-platinum/10">
            <AvatarFallback className="bg-mono-onyx/70 text-mono-seasalt text-xs">
              {user.email?.substring(0, 2).toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium text-mono-seasalt hidden md:inline">
            {user.email?.split('@')[0] || 'User'}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-48 bg-mono-eerieBlack/95 border border-mono-slateGray/20 shadow-xl" align="end">
        <DropdownMenuLabel className="flex items-center space-x-2 text-mono-platinum">
          <UserIcon className="h-4 w-4 text-mono-platinum/70" />
          <span>My account</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-mono-slateGray/20" />
        <DropdownMenuItem
          className="cursor-pointer text-mono-platinum focus:bg-mono-outerSpace/50 focus:text-mono-seasalt hover:bg-mono-outerSpace/50 hover:text-mono-seasalt transition-colors duration-200"
          onClick={signOut}
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AuthStatus;
